import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import dayjs from 'dayjs';
import { useCurrentPlan } from '@/lib/permissions/usePlanQuota';
import { billingApi } from './api';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { UsageMeter } from './UsageMeter';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';

// Rows to render in the Usage card. Each row resolves either to a shared cap
// (read off the slim CoCompanyPlanUsage — used count maintained separately),
// or to a per-app per-resource row from `current.resources[]` (canonical
// post-normalization). See docs/prds/universal/plan-usage-normalization.md.
type UsageSource =
  | {
      kind: 'shared';
      cap:
        | 'maxBrands'
        | 'maxUsers'
        | 'maxRoles'
        | 'maxSurveys'
        | 'maxResponsesPerSurvey';
      usedField?: 'brandsUsed' | 'usersUsed' | 'rolesUsed' | 'surveysUsed';
    }
  | { kind: 'resource'; app: string; resource: string };

const RESOURCES: Array<{ labelKey: string; source: UsageSource }> = [
  { labelKey: 'billingPage.resource.brands',      source: { kind: 'shared',   cap: 'maxBrands', usedField: 'brandsUsed' } },
  { labelKey: 'billingPage.resource.users',       source: { kind: 'shared',   cap: 'maxUsers',  usedField: 'usersUsed' } },
  { labelKey: 'billingPage.resource.roles',       source: { kind: 'shared',   cap: 'maxRoles',  usedField: 'rolesUsed' } },
  { labelKey: 'billingPage.resource.surveys',     source: { kind: 'shared',   cap: 'maxSurveys',usedField: 'surveysUsed' } },
  { labelKey: 'billingPage.resource.eventGroups', source: { kind: 'resource', app: 'xotski',    resource: 'event_group' } },
  { labelKey: 'billingPage.resource.events',      source: { kind: 'resource', app: 'xotski',    resource: 'event' } },
  { labelKey: 'billingPage.resource.stations',    source: { kind: 'resource', app: 'xotski',    resource: 'station' } },
  { labelKey: 'billingPage.resource.stocks',      source: { kind: 'resource', app: 'xotski',    resource: 'stock' } },
];

// Boolean feature flags on the plan. Rendered as on/off pills — order matters
// (most-asked first). Field names mirror `CoCompanyPlanUsage` columns.
const FEATURES: Array<{ field: string; labelKey: string }> = [
  { field: 'featNotifications',  labelKey: 'billingPage.feature.notifications' },
  { field: 'featActiveLog',      labelKey: 'billingPage.feature.activeLog' },
  { field: 'featAiAssistant',    labelKey: 'billingPage.feature.aiAssistant' },
  { field: 'featStockImport',    labelKey: 'billingPage.feature.stockImport' },
  { field: 'featUserManagement', labelKey: 'billingPage.feature.userManagement' },
];

// Friendly labels for game IDs. Anything not in the map renders raw.
const GAME_LABELS: Record<string, string> = {
  wheel: 'Prize Wheel',
  scratch: 'Scratch Card',
  pirinola: 'Pirinola',
  'random-draw': 'Random Draw',
  plinko: 'Plinko',
  trivia: 'Trivia',
};

// Invoice status → badge tone + i18n label. Keyed by the backend
// `InvoicePlanStatus` enum (Active/Expired/Refunded/Exhausted).
type BadgeTone = 'success' | 'neutral' | 'info' | 'warn';
const INVOICE_STATUS: Record<string, { tone: BadgeTone; key: string }> = {
  Active: { tone: 'success', key: 'billingPage.invoice.status.active' },
  Expired: { tone: 'neutral', key: 'billingPage.invoice.status.expired' },
  Refunded: { tone: 'info', key: 'billingPage.invoice.status.refunded' },
  Exhausted: { tone: 'warn', key: 'billingPage.invoice.status.exhausted' },
};
// Defensive fallback for an unknown / unmapped status value.
const INVOICE_STATUS_FALLBACK: { tone: BadgeTone; key: string } = {
  tone: 'warn',
  key: 'billingPage.invoice.pending',
};

/**
 * Format an invoice amount. TypeORM serialises DECIMAL columns as strings
 * ("3500.00"), so coerce to a number and render with grouping + 2 decimals.
 */
function formatAmount(raw: number | string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return `$${raw}`;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function BillingPage() {
  const navigate = useNavigate();
  const t = useT();
  useFavoritable(t('billing.title'));
  const { data: current, isLoading } = useCurrentPlan();
  const { data: invoices } = useQuery({
    queryKey: queryKeys.plans.invoices,
    queryFn: billingApi.listInvoices,
  });
  // `brandsUsed` on the usage row is not maintained server-side (the validator
  // counts at mutation time). Use the live brand listing — same source the
  // Home gate already reads — so the meter matches reality.
  const { data: brandsData } = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const brandsUsed = brandsData?.items.length ?? 0;

  const games = (current?.allowedGames ?? '')
    .split(',')
    .map((g: string) => g.trim())
    .filter(Boolean);
  const responsesPerSurvey = current?.maxResponsesPerSurvey ?? 0;

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          {t('billingPage.title')}
          <HelpHint section="billing" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('billingPage.subtitle')}
        </p>
      </header>

      {/* Current plan */}
      <Card>
        <CardHeader>
          <Badge tone="accent" className="mr-auto">
            {t('billingPage.currentBadge')}
          </Badge>
          <CardTitle>
            {isLoading ? <Skeleton className="h-7 w-32" /> : t('billingPage.currentTitle')}
          </CardTitle>
          <CardDescription>
            {t('billingPage.currentDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="primary" onClick={() => navigate('/pricing')}>
            {t('billingPage.changePlan')}
          </Button>
        </CardContent>
      </Card>

      {/* Usage */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billingPage.usageTitle')}</CardTitle>
          <CardDescription>{t('billingPage.usageDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!current ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {RESOURCES.map((r) => {
                const { used, limit } = resolveUsage(current, r.source, { brandsUsed });
                return (
                  <UsageMeter
                    key={r.labelKey}
                    label={t(r.labelKey)}
                    used={used}
                    limit={limit}
                  />
                );
              })}
              {/* Responses per survey is a per-survey ceiling, not a counter
                  — render as a stat card to set expectations. */}
              <ResponsesPerSurveyStat
                label={t('billingPage.resource.responsesPerSurvey')}
                value={responsesPerSurvey}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan features (boolean flags) */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billingPage.featuresTitle')}</CardTitle>
          <CardDescription>{t('billingPage.featuresDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!current ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f) => {
                const enabled = Boolean(
                  (current as unknown as Record<string, boolean>)[f.field],
                );
                return (
                  <FeaturePill key={f.field} label={t(f.labelKey)} enabled={enabled} />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allowed games */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billingPage.gamesTitle')}</CardTitle>
          <CardDescription>{t('billingPage.gamesDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!current ? (
            <Skeleton className="h-10" />
          ) : games.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('billingPage.gamesEmpty')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {games.map((g) => (
                <Badge key={g} tone="accent">
                  {GAME_LABELS[g] ?? g}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billingPage.invoicesTitle')}</CardTitle>
          <CardDescription>{t('billingPage.invoicesDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {!invoices || invoices.items.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">{t('billingPage.noInvoices')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left font-semibold">{t('billingPage.col.date')}</th>
                  <th className="py-2 text-left font-semibold">{t('billingPage.col.plan')}</th>
                  <th className="py-2 text-right font-semibold">{t('billingPage.col.amount')}</th>
                  <th className="py-2 text-right font-semibold">{t('billingPage.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.items.map((inv) => {
                  const status =
                    INVOICE_STATUS[inv.planStatus] ?? INVOICE_STATUS_FALLBACK;
                  return (
                    <tr key={inv.id} className="border-t border-[var(--color-border-subtle)]">
                      <td className="py-3">{dayjs(inv.createdAt).format('MMM D, YYYY')}</td>
                      <td className="py-3">{inv.plan?.name ?? `#${inv.planId}`}</td>
                      <td className="py-3 text-right tabular-nums">
                        {formatAmount(inv.amount)}
                      </td>
                      <td className="py-3 text-right">
                        <Badge tone={status.tone}>{t(status.key)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function resolveUsage(
  current: NonNullable<ReturnType<typeof useCurrentPlan>['data']>,
  source: UsageSource,
  overrides: { brandsUsed?: number } = {},
): { used: number; limit: number } {
  if (source.kind === 'shared') {
    const cap = (current as unknown as Record<string, number>)[source.cap] ?? 0;
    // `brandsUsed` on the usage row is not maintained server-side, so accept
    // an override (caller passes the live `brandsApi.list()` count).
    if (source.cap === 'maxBrands' && overrides.brandsUsed != null) {
      return { used: overrides.brandsUsed, limit: cap };
    }
    const used = source.usedField
      ? (current as unknown as Record<string, number>)[source.usedField] ?? 0
      : 0;
    return { used, limit: cap };
  }
  const row = current.resources?.find(
    (r) => r.app === source.app && r.resource === source.resource,
  );
  return { used: row?.used ?? 0, limit: row?.limit ?? 0 };
}

function FeaturePill({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div
      className="flex items-center justify-between gap-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
    >
      <span
        className={
          enabled
            ? 'text-[var(--color-fg-primary)]'
            : 'text-[var(--color-fg-muted)]'
        }
      >
        {label}
      </span>
      {enabled ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-accent-soft,rgba(178,98,245,0.12))] px-2 py-0.5 text-xs font-semibold text-[var(--color-accent,#B262F5)]">
          <Check className="h-3 w-3" /> On
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs font-semibold text-[var(--color-fg-subtle)]">
          <Lock className="h-3 w-3" /> Off
        </span>
      )}
    </div>
  );
}

function ResponsesPerSurveyStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">
        {value > 0 ? value.toLocaleString() : '∞'}
      </div>
      <div className="text-xs text-[var(--color-fg-subtle)]">per journey</div>
    </div>
  );
}
